import { forwardRef, useEffect, useImperativeHandle } from 'react';
import {
  Divider,
  DropdownList,
  Stack,
  Text,
  ToggleListItem,
} from '@adc/parallax-component-library';
import { useForm, useStore } from '@tanstack/react-form';

import { DropdownInput } from '~/components/DropdownInput';
import {
  type PatientSettingsFormProps,
  type PatientSettingsFormRef,
} from '~/features/patientCreation/types';
import { buildTestId } from '~/utils/buildTestId';
import { getErrorProps } from '~/utils/forms/getErrorProps';

import {
  HIGH_LACTATE_ALERT_DURATION_ITEMS,
  HIGH_LACTATE_ALERT_ITEMS,
  PATIENT_ALERT_DEFAULT_VALUES,
} from './constants';
import { patientSettingsSchema } from './validations';

export const PatientSettingsForm = forwardRef<
  PatientSettingsFormRef,
  PatientSettingsFormProps
>(({ testID, onSubmit, defaultValues, hasFormChanged }, ref) => {
  const form = useForm({
    defaultValues: { ...PATIENT_ALERT_DEFAULT_VALUES, ...defaultValues },
    validators: { onChange: patientSettingsSchema },
    onSubmit: ({ value }) => {
      void onSubmit?.(value);
    },
  });

  useImperativeHandle(
    ref,
    () => ({
      submitForm: form.handleSubmit,
      getValues: () => form.state.values,
    }),
    [form],
  );

  useEffect(() => {
    if (hasFormChanged) {
      hasFormChanged(form.state.isDirty);
    }
  }, [form.state.isDirty, hasFormChanged]);

  const { highLactateAlert } = useStore(form.store, (s) => s.values);

  return (
    <Stack testID={testID}>
      {/* Display Lactate Data */}
      <Stack marginHorizontal={-16}>
        <form.Field name="displayLactateData">
          {(field) => (
            <ToggleListItem
              testID={buildTestId({
                testID,
                suffix: ['toggle', 'display', 'lactate', 'data'],
              })}
              title="Display patient’s lactate data"
              description="When turned “On”, lactate values will be visible to the patient on their mobile app. When “Off,” patients will not see lactate values but will still receive high lactate alerts"
              checked={field.state.value}
              onPress={(checked) => {
                field.handleChange(checked);
              }}
            />
          )}
        </form.Field>
      </Stack>

      <Divider backgroundColor="$gray4Light" marginBottom="$4" />
      {/* High Lactate Alert Toggle */}
      <Stack marginHorizontal={-16}>
        <form.Field name="highLactateAlert">
          {(field) => (
            <ToggleListItem
              testID={buildTestId({
                testID,
                suffix: ['toggle', 'high', 'lactate', 'alert'],
              })}
              title="High Lactate Alert"
              checked={field.state.value}
              onPress={(checked) => {
                field.handleChange(checked);
              }}
            />
          )}
        </form.Field>
      </Stack>

      {/* High Lactate Alert Settings */}
      {highLactateAlert && (
        <Stack gap="$4">
          <Text type="$bodySmall.default" color="$text.60">
            This alert will be triggered when the patient’s lactate value is:
          </Text>
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            gap="$6"
            marginBottom="$5"
          >
            {/* Alert Above */}
            <Stack flex={1}>
              <form.Field name="highLactateAlertAbove">
                {(field) => {
                  const selectedIndex = HIGH_LACTATE_ALERT_ITEMS.findIndex(
                    (i) => i.value === field.state.value,
                  );
                  const selectedLabel =
                    selectedIndex >= 0
                      ? HIGH_LACTATE_ALERT_ITEMS[selectedIndex]?.label
                      : '';

                  return (
                    <DropdownList
                      testID={buildTestId({
                        testID,
                        suffix: [
                          'dropdown',
                          'high',
                          'lactate',
                          'alert',
                          'above',
                        ],
                      })}
                      items={HIGH_LACTATE_ALERT_ITEMS.map((i) => i.label)}
                      selectedIndex={
                        selectedIndex >= 0 ? selectedIndex : undefined
                      }
                      onSelect={(index) => {
                        const selected = HIGH_LACTATE_ALERT_ITEMS[index];

                        if (selected) {
                          field.handleChange(selected.value); // store mmol/L value
                          field.handleBlur();
                        }
                      }}
                    >
                      <DropdownInput
                        testID={buildTestId({
                          testID,
                          suffix: [
                            'input',
                            'high',
                            'lactate',
                            'alert',
                            'above',
                          ],
                        })}
                        aria-label="High Lactate Alert Above"
                        label="At or above"
                        value={selectedLabel}
                        {...getErrorProps(field)}
                      />
                    </DropdownList>
                  );
                }}
              </form.Field>
            </Stack>
            {/* Alert Duration */}
            <Stack flex={1}>
              <form.Field name="highLactateAlertDuration">
                {(field) => {
                  const selectedIndex =
                    HIGH_LACTATE_ALERT_DURATION_ITEMS.findIndex(
                      (i) => i.value === field.state.value,
                    );
                  const selectedLabel =
                    selectedIndex >= 0
                      ? HIGH_LACTATE_ALERT_DURATION_ITEMS[selectedIndex]?.label
                      : '';

                  return (
                    <DropdownList
                      testID={buildTestId({
                        testID,
                        suffix: [
                          'dropdown',
                          'high',
                          'lactate',
                          'alert',
                          'duration',
                        ],
                      })}
                      items={HIGH_LACTATE_ALERT_DURATION_ITEMS.map(
                        (i) => i.label,
                      )}
                      selectedIndex={
                        selectedIndex >= 0 ? selectedIndex : undefined
                      }
                      onSelect={(index) => {
                        const selected =
                          HIGH_LACTATE_ALERT_DURATION_ITEMS[index];

                        if (selected) {
                          field.handleChange(selected.value);
                          field.handleBlur();
                        }
                      }}
                    >
                      <DropdownInput
                        testID={buildTestId({
                          testID,
                          suffix: [
                            'input',
                            'high',
                            'lactate',
                            'alert',
                            'duration',
                          ],
                        })}
                        aria-label="High Lactate Alert Duration"
                        label="For at least"
                        value={selectedLabel}
                        {...getErrorProps(field)}
                      />
                    </DropdownList>
                  );
                }}
              </form.Field>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});

PatientSettingsForm.displayName = 'PatientSettingsForm';
